import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, LogOut, ArrowLeft, X } from "lucide-react";
import { API_URL, BRAND } from "../lib/brand";

export default function AdminDashboard() {
  const [posts, setPosts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [tab, setTab] = useState("blog");
  const navigate = useNavigate();

  const token = localStorage.getItem("tv_admin_token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) { navigate("/admin/login"); return; }
    load();
  }, []);

  const load = async () => {
    try {
      const [b, bo] = await Promise.all([
        axios.get(`${API_URL}/blog`),
        axios.get(`${API_URL}/admin/bookings`, auth),
      ]);
      setPosts(b.data);
      setBookings(bo.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("tv_admin_token");
        navigate("/admin/login");
      }
    }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo articolo?")) return;
    try {
      await axios.delete(`${API_URL}/admin/blog/${id}`, auth);
      toast.success("Articolo eliminato");
      load();
    } catch { toast.error("Errore"); }
  };

  const logout = () => {
    localStorage.removeItem("tv_admin_token");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="admin-dashboard">
      <header className="border-b border-[#81C3C9]/30 bg-white sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="Logo" className="h-10 w-10 rounded-full" />
            <div>
              <p className="font-display text-xl text-[#3D2140]">Admin</p>
              <p className="text-[10px] uppercase tracking-widest text-[#FF2768]">Teresa Vilardo</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/magazine" className="text-sm font-semibold text-[#3D2140] hover:text-[#FF2768] inline-flex items-center gap-1" data-testid="dashboard-link-magazine">📖 Magazine</Link>
            <Link to="/admin/voucher" className="text-sm font-semibold text-[#3D2140] hover:text-[#FF2768] inline-flex items-center gap-1" data-testid="dashboard-link-voucher">🎁 Voucher</Link>
            <Link to="/admin/richieste-info" className="text-sm font-semibold text-[#3D2140] hover:text-[#FF2768] inline-flex items-center gap-1" data-testid="dashboard-link-richieste">💬 Richieste</Link>
            <Link to="/admin/newsletter" className="text-sm font-semibold text-[#3D2140] hover:text-[#FF2768] inline-flex items-center gap-1" data-testid="dashboard-link-newsletter">💌 Newsletter</Link>
            <Link to="/admin/avvento" className="text-sm font-semibold text-[#3D2140] hover:text-[#FF2768] inline-flex items-center gap-1" data-testid="dashboard-link-avvento">🎄 Avvento 2026</Link>
            <Link to="/admin/statistiche" className="text-sm font-semibold text-[#3D2140] hover:text-[#FF2768] inline-flex items-center gap-1" data-testid="dashboard-link-stats">📊 Statistiche</Link>
            <Link to="/admin/risorse" className="text-sm font-semibold text-[#FF2768] hover:underline inline-flex items-center gap-1">📚 Biblioteca T.A.T.A.</Link>
            <Link to="/" className="text-sm text-[#3D2140]/70 hover:text-[#FF2768] inline-flex items-center gap-1"><ArrowLeft size={14} /> Sito</Link>
            <button onClick={logout} data-testid="admin-logout" className="btn-tv-outline text-sm py-2 px-4"><LogOut size={14} /> Esci</button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="flex items-center gap-2 mb-8 border-b border-[#81C3C9]/40">
          {["blog", "bookings"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t}`}
              className={`px-6 py-3 font-semibold uppercase text-sm tracking-wider transition-colors border-b-2 -mb-px ${tab === t ? "text-[#FF2768] border-[#FF2768]" : "text-[#3D2140]/50 border-transparent"}`}
            >
              {t === "blog" ? `Articoli (${posts.length})` : `Prenotazioni (${bookings.length})`}
            </button>
          ))}
        </div>

        <TataLandingCard auth={auth} />

        {tab === "blog" && (
          <>
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-display text-5xl md:text-6xl text-[#3D2140]">Blog</h1>
              <button
                onClick={() => { setEditing(null); setShowForm(true); }}
                data-testid="admin-new-post"
                className="btn-tv-primary"
              >
                <Plus size={16} /> Nuovo articolo
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-[#81C3C9]/30 overflow-hidden">
              {posts.length === 0 && <p className="p-8 text-[#3D2140]/60 text-center">Nessun articolo. Creane uno!</p>}
              {posts.map((p) => (
                <div key={p.id} className="flex items-center gap-4 p-5 border-b border-[#81C3C9]/20 last:border-0 hover:bg-[#FAF8F5] transition-colors">
                  {p.cover_image ? <img src={p.cover_image} alt="" className="h-14 w-14 rounded-lg object-cover" /> : <div className="h-14 w-14 rounded-lg bg-[#81C3C9]/25" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#3D2140] truncate">{p.title}</p>
                    <p className="text-sm text-[#3D2140]/50 truncate">{p.excerpt}</p>
                  </div>
                  <button onClick={() => { setEditing(p); setShowForm(true); }} data-testid={`admin-edit-${p.id}`} className="h-9 w-9 rounded-full bg-[#81C3C9]/20 hover:bg-[#81C3C9]/40 flex items-center justify-center"><Edit3 size={14} /></button>
                  <button onClick={() => del(p.id)} data-testid={`admin-delete-${p.id}`} className="h-9 w-9 rounded-full bg-[#FF2768]/10 hover:bg-[#FF2768]/25 text-[#FF2768] flex items-center justify-center"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "bookings" && (
          <>
            <h1 className="font-display text-5xl md:text-6xl text-[#3D2140] mb-8">Prenotazioni</h1>
            <div className="bg-white rounded-2xl border border-[#81C3C9]/30 overflow-hidden divide-y divide-[#81C3C9]/20">
              {bookings.length === 0 && <p className="p-8 text-[#3D2140]/60 text-center">Nessuna prenotazione ricevuta.</p>}
              {bookings.map((b) => (
                <div key={b.id} className="p-5">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div>
                      <p className="font-semibold text-[#3D2140] text-lg">{b.name}</p>
                      <p className="text-sm text-[#3D2140]/60">{b.email} · {b.phone}</p>
                    </div>
                    <p className="text-xs text-[#81C3C9] whitespace-nowrap">{new Date(b.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}</p>
                  </div>
                  <p className="text-sm font-medium text-[#FF2768] mb-2">{b.topic}{b.preferred_date && ` · ${b.preferred_date}`}</p>
                  <p className="text-[#3D2140]/80 text-sm whitespace-pre-wrap">{b.message}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {showForm && (
        <BlogForm
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
          auth={auth}
        />
      )}
    </div>
  );
}

function BlogForm({ initial, onClose, onSaved, auth }) {
  const [f, setF] = useState({
    title: initial?.title || "",
    excerpt: initial?.excerpt || "",
    content: initial?.content || "",
    cover_image: initial?.cover_image || "",
    tags: (initial?.tags || []).join(", "),
  });
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...f, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (isEdit) await axios.put(`${API_URL}/admin/blog/${initial.id}`, payload, auth);
      else await axios.post(`${API_URL}/admin/blog`, payload, auth);
      toast.success(isEdit ? "Articolo aggiornato" : "Articolo creato");
      onSaved();
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-[#3D2140]/60 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-2xl w-full my-8 p-8 md:p-10 relative" onClick={(e) => e.stopPropagation()} data-testid="admin-blog-form">
        <button onClick={onClose} className="absolute top-4 right-4 h-9 w-9 rounded-full bg-[#FAF8F5] flex items-center justify-center"><X size={16} /></button>
        <h2 className="font-display text-4xl text-[#3D2140] mb-6">{isEdit ? "Modifica articolo" : "Nuovo articolo"}</h2>
        <form onSubmit={submit} className="space-y-5">
          <AdminField label="Titolo" value={f.title} onChange={(v) => setF({ ...f, title: v })} required testId="blog-title" />
          <AdminField label="Estratto" value={f.excerpt} onChange={(v) => setF({ ...f, excerpt: v })} required testId="blog-excerpt" />
          <AdminField label="Immagine cover (URL)" value={f.cover_image} onChange={(v) => setF({ ...f, cover_image: v })} testId="blog-cover" />
          <AdminField label="Tag (separati da virgola)" value={f.tags} onChange={(v) => setF({ ...f, tags: v })} testId="blog-tags" />
          <div>
            <label className="block text-[10px] uppercase tracking-[0.3em] text-[#81C3C9] mb-2 font-semibold">Contenuto</label>
            <textarea
              value={f.content}
              onChange={(e) => setF({ ...f, content: e.target.value })}
              required
              rows={10}
              data-testid="blog-content"
              className="w-full border-2 border-[#81C3C9]/30 rounded-xl p-4 focus:border-[#FF2768] transition-colors resize-none outline-none"
            />
          </div>
          <button type="submit" disabled={saving} data-testid="blog-save" className="btn-tv-primary">
            {saving ? "Salvataggio…" : (isEdit ? "Salva modifiche" : "Pubblica articolo")}
          </button>
        </form>
      </div>
    </div>
  );
}

function AdminField({ label, value, onChange, required, testId }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.3em] text-[#81C3C9] mb-2 font-semibold">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testId}
        className="w-full border-b-2 border-[#3D2140]/20 bg-transparent py-2.5 focus:border-[#FF2768] transition-colors outline-none"
      />
    </div>
  );
}

/** Controllo della landing "Become Tata Like a Pro": pubblicazione + date edizione */
function TataLandingCard({ auth }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/admin/tata-landing/settings`, auth).then((r) => setS(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  if (!s) return null;

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API_URL}/admin/tata-landing/settings`, { ...s, ...patch }, auth);
      setS(r.data);
      toast.success("Landing aggiornata");
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#3D2140]/10 p-6 mb-10" data-testid="tata-landing-card">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div>
          <h2 className="font-display text-2xl text-[#3D2140]">Landing "Become Tata Like a Pro"</h2>
          <p className="text-xs text-[#3D2140]/60 mt-1">
            Pagina: <a href="/become-tata-like-a-pro" target="_blank" rel="noopener noreferrer" className="text-[#FF2768] underline">/become-tata-like-a-pro</a> — invisibile al pubblico finché è spenta.
          </p>
        </div>
        <button
          onClick={() => save({ page_published: !s.page_published })}
          data-testid="toggle-tata-landing"
          className={`px-5 py-3 rounded-full font-semibold text-sm ${s.page_published ? "bg-[#81C3C9] text-[#3D2140]" : "bg-[#3D2140] text-white"}`}
        >
          {s.page_published ? "Pagina ATTIVA (visibile)" : "Pagina NON attiva"}
        </button>
      </div>
      <p className="text-[10px] uppercase tracking-widest text-[#3D2140]/50 font-bold mb-2">Prossima edizione (modificabile per le future edizioni — lascia vuoti per non mostrare date: la pagina inviterà a chiedere su WhatsApp)</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[["edition_period", "Periodo"], ["edition_days", "Giorni"], ["edition_time", "Orario"]].map(([k, label]) => (
          <div key={k}>
            <label className="text-[10px] uppercase tracking-widest text-[#3D2140]/50 font-bold">{label}</label>
            <input
              value={s[k] || ""}
              onChange={(e) => setS({ ...s, [k]: e.target.value })}
              data-testid={`tata-${k}`}
              className="w-full border-b-2 border-[#3D2140]/20 bg-transparent py-2 focus:border-[#FF2768] outline-none text-[#3D2140]"
            />
          </div>
        ))}
      </div>
      <button onClick={() => save({})} disabled={saving} data-testid="tata-save-edition" className="mt-4 px-6 py-2.5 rounded-full bg-[#FF2768] text-white text-sm font-semibold disabled:opacity-50">
        {saving ? "Salvo…" : "Salva date edizione"}
      </button>
    </div>
  );
}
